import type { RaceConfig } from '../../types/config.ts';
import type { NormalizedResult } from '../../types/results.ts';
import type { ProviderState } from '../../types/ui.ts';
import type { Reconciliation } from '../../lib/reconcile.ts';
import { candidateById } from '../../lib/candidates.ts';
import { candidateColor } from '../../lib/colors.ts';
import { BarRow } from '../charts/BarRow.tsx';
import { Provenance } from '../common/Provenance.tsx';
import { SourceState } from '../common/SourceState.tsx';
import { MultiSourceStrip } from './MultiSourceStrip.tsx';
import { DivergenceFlag } from './DivergenceFlag.tsx';

type VoteStates = Record<string, ProviderState<NormalizedResult>>;

function key(c: { votes: number | null; percent: number | null }): number {
  return c.votes ?? c.percent ?? -Infinity;
}

/** Per-candidate bars from the primary source, the multi-source strip, the
 *  divergence flag, and honest cards for any vote source that didn't succeed. */
export function CountDetail({
  race,
  reconciliation,
  voteStates,
  resolved,
}: {
  race: RaceConfig;
  reconciliation: Reconciliation;
  voteStates: VoteStates;
  resolved: 'dark' | 'light';
}) {
  const primary = reconciliation.primary;
  const leaderId = reconciliation.primaryMargin?.leaderId ?? null;

  return (
    <div className="count">
      {primary ? (
        <div className="count__bars">
          {[...primary.candidates]
            .sort((a, b) => key(b) - key(a))
            .map((c) => {
              const cand = candidateById(race, c.id);
              if (!cand) return null;
              return (
                <BarRow
                  key={c.id}
                  candidate={cand}
                  color={candidateColor(cand, resolved)}
                  percent={c.percent}
                  votes={c.votes}
                  leading={c.id === leaderId}
                />
              );
            })}
          <Provenance
            source={primary.source}
            fetchedAt={primary.fetchedAt}
            isStale={voteStates[primary.sourceId]?.isStale}
            entry={primary.entry}
          />
        </div>
      ) : (
        <SourceState
          label="Count"
          kind={null}
          loading={Object.values(voteStates).some((s) => s.status === 'loading' || s.status === 'idle')}
        />
      )}

      <DivergenceFlag divergence={reconciliation.divergence} thresholdPct={race.divergenceFlagPct} />
      <MultiSourceStrip race={race} items={reconciliation.strip} />

      {/* Every vote source is accounted for: successful ones appear above / in
          the strip; the rest show an honest status card (e.g. AP not configured). */}
      {race.voteProviders
        .filter((p) => p.enabled)
        .map((p) => {
          const st = voteStates[p.id];
          if (!st || st.status === 'success') return null;
          const isPrimaryShown = primary != null && primary.sourceId === p.id;
          if (isPrimaryShown) return null;
          return (
            <SourceState
              key={p.id}
              label={p.label}
              kind={st.status === 'loading' || st.status === 'idle' ? null : st.errorKind}
              loading={st.status === 'loading' || st.status === 'idle'}
            />
          );
        })}
    </div>
  );
}
